import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useState } from 'react';

import { Button as OutlineButton } from '@/components/shadcn/ui/button';
import { updateSignalMutationOptions } from '@/entities/signal';
import { errorHandler } from '@/error-handler';
import usePreventUnsaved from '@/hooks/pure/usePreventUnsaved';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';
import { loadingToast, toast } from '@/stores/pure/ToastStore';

import {
  type CreateSignalFormData,
  createSignalFormOptions,
} from '../model/schema';
import { SignalFormContent } from './signal-form-content';

// Hook을 사용하기 위한 wrapper component
function BlockRefresh({ isBlocked }: { isBlocked: boolean }) {
  usePreventUnsaved({ notSaved: isBlocked });
  return <></>;
}

export type UpdateSignalDialogProps = {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  defaultValues: CreateSignalFormData;
  workspaceId: string;
  signalId: string;
};

export function UpdateSignalDialog({
  isOpen,
  close,
  unmount,
  defaultValues,
  workspaceId,
  signalId,
}: UpdateSignalDialogProps) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation(updateSignalMutationOptions(queryClient));
  const [hasValidPreview, setHasValidPreview] = useState(false);

  const handlePreviewComplete = useCallback(() => {
    setHasValidPreview(true);
  }, []);

  const handleFormValueChange = useCallback(() => {
    setHasValidPreview(false);
  }, []);

  const form = useAppForm({
    ...createSignalFormOptions,
    defaultValues,
    onSubmit: async ({ value }) => {
      const dismissToast = loadingToast(
        'Processing reclassification',
        'Please wait while we update.',
      );

      try {
        await mutateAsync({
          workspace_id: workspaceId,
          signal_id: signalId,
          title: value.title,
          definition: value.definition,
        });
        toast('Signal updated', 'success');
      } catch (error) {
        toast(errorHandler(error, 'Failed to update signal'), 'fail');
      } finally {
        dismissToast();
      }
      close();
    },
  });

  return (
    <form.Subscribe selector={(state) => [state.isSubmitting]}>
      {([isSubmitting]) => (
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            if (!open && !isSubmitting) close();
          }}
          onExitComplete={unmount}
        >
          <DialogContent size="xl">
            <DialogHeader>
              <DialogTitle>Edit signal</DialogTitle>
              <DialogDescription>
                Update the signal definition. Issues will be reclassified after
                saving.
              </DialogDescription>
            </DialogHeader>

            <DialogBody className="flex-1 min-h-0">
              <BlockRefresh isBlocked={isSubmitting} />
              <Suspense fallback={<></>}>
                <SignalFormContent
                  signalId={signalId}
                  form={form}
                  workspaceId={workspaceId}
                  isSubmitting={isSubmitting}
                  originalTitle={defaultValues.title}
                  onPreviewComplete={handlePreviewComplete}
                  onFormValueChange={handleFormValueChange}
                />
              </Suspense>
            </DialogBody>

            <DialogFooter>
              <div />
              <div className="flex gap-2">
                <OutlineButton
                  variant="outline"
                  onClick={close}
                  disabled={isSubmitting}
                >
                  Cancel
                </OutlineButton>
                <form.Subscribe selector={(state) => state.isDefaultValue}>
                  {(isDefaultValue) => (
                    <Button
                      variant="default"
                      isLoading={isSubmitting}
                      disabled={isDefaultValue || !hasValidPreview}
                      onClick={async () => {
                        // 클릭 시 모든 필드 유효성 검사 트리거
                        await form.validateAllFields('change');
                        if (!form.state.isFieldsValid) return;
                        await form.handleSubmit();
                      }}
                    >
                      Save
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </form.Subscribe>
  );
}
